import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getInitData } from '@/lib/tg';
import type {
  QuizAttemptDTO,
  QuizQuestionDTO,
  QuizAnswerResultDTO,
  QuizFinishDTO,
} from '@amrita/shared';
import { EXAM_PASS_SCORE, passScorePct } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Mic,
  Square,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { notifyOk, notifyErr, haptic, tg } from '@/lib/tg';

/** Локальная запись разбора по одному вопросу — собирается по ходу прохождения. */
interface ReviewEntry {
  questionId: string;
  questionMd: string;
  options: string[] | null;
  /** Текст ответа пользователя (для MC — выбранный вариант, для free-form — введённый текст). */
  userAnswer: string;
  /** Индекс выбранного варианта (только MC, иначе null). */
  selectedOption: number | null;
  score: number;
  feedback: string;
  correct?: boolean;
  correctOption?: number;
}

/** Запись голоса через MediaRecorder → webm → upload → text. */
function useVoiceRecorder(onText: (text: string) => void) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'answer.webm');
          const res = await fetch('/api/voice/transcribe', {
            method: 'POST',
            headers: { 'X-Telegram-Init-Data': getInitData() },
            body: fd,
          });
          const json = res.ok ? await res.json().catch(() => null) : null;
          if (json?.ok && json.data?.text) {
            onText(json.data.text);
          } else {
            tg()?.showAlert('Не удалось распознать речь. Введи ответ текстом.');
          }
        } finally {
          setBusy(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      haptic('light');
    } catch (e) {
      alert('Нет доступа к микрофону: ' + (e as Error).message);
    }
  }

  function stop() {
    recRef.current?.stop();
    setRecording(false);
    haptic('light');
  }

  return { recording, busy, start, stop };
}

export default function QuizPage() {
  const { id: lessonId } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const examMode = sp.get('exam') === '1';
  const nav = useNavigate();

  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [showFreeForm, setShowFreeForm] = useState(false);
  const [lastFeedback, setLastFeedback] = useState<QuizAnswerResultDTO | null>(null);
  const [finished, setFinished] = useState<QuizFinishDTO | null>(null);
  const [review, setReview] = useState<ReviewEntry[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const voice = useVoiceRecorder((t) => setAnswer((prev) => (prev ? prev + ' ' + t : t)));

  const { data: questions, isLoading } = useQuery<QuizQuestionDTO[]>({
    queryKey: ['quiz-questions', lessonId, examMode],
    queryFn: () =>
      api.get<QuizQuestionDTO[]>(
        `/api/training/lessons/${lessonId}/questions${examMode ? '?exam=1' : ''}`,
      ),
    enabled: !!lessonId,
  });

  useEffect(() => {
    if (!attemptId && questions && questions.length > 0 && lessonId) {
      api
        .post<QuizAttemptDTO>('/api/training/attempts', { lessonId, isFinalExam: examMode })
        .then((a) => setAttemptId(a.id))
        .catch(() => {});
    }
  }, [questions, attemptId, lessonId, examMode]);

  const q = questions?.[idx];
  const isMC = Array.isArray(q?.options) && (q?.options?.length ?? 0) >= 2;

  const submit = useMutation<QuizAnswerResultDTO, Error, void>({
    mutationFn: () => {
      if (!q) throw new Error('no question');
      const body: { questionId: string; answer?: string; selectedOption?: number } = {
        questionId: q.id,
      };
      if (isMC && selected !== null) {
        body.selectedOption = selected;
        body.answer = (q.options as string[])[selected];
      } else {
        body.answer = answer;
      }
      return api.post<QuizAnswerResultDTO>(`/api/training/attempts/${attemptId}/answer`, body);
    },
    onSuccess: (r) => {
      setLastFeedback(r);
      // Собираем разбор по текущему вопросу.
      if (q) {
        const entry: ReviewEntry = {
          questionId: q.id,
          questionMd: q.questionMd,
          options: q.options ?? null,
          userAnswer:
            isMC && selected !== null
              ? ((q.options ?? [])[selected] ?? '')
              : answer.trim(),
          selectedOption: isMC && selected !== null ? selected : null,
          score: r.score,
          feedback: r.feedback,
          correct: r.correct,
          correctOption: r.correctOption,
        };
        setReview((prev) => {
          const next = prev.filter((e) => e.questionId !== q.id);
          next.push(entry);
          return next;
        });
      }
      if (r.score >= 7) notifyOk();
      else notifyErr();
    },
    onError: () => {
      notifyErr();
      tg()?.showAlert('Не удалось проверить ответ — попробуй ещё раз.');
    },
  });

  const finish = useMutation<QuizFinishDTO, Error, void>({
    mutationFn: () => api.post<QuizFinishDTO>(`/api/training/attempts/${attemptId}/finish`, {}),
    onSuccess: (r) => setFinished(r),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full gap-4 p-6 text-center">
        <Loader2 className="w-12 h-12 text-brand-orange animate-spin" />
        <div className="text-lg font-bold">{examMode ? 'Готовлю экзамен…' : 'Генерирую тест…'}</div>
        <p className="text-sm text-tg-hint max-w-xs">
          {examMode
            ? 'Подбираю вопросы по курсу — это займёт несколько секунд'
            : 'ИИ составляет вопросы по уроку — обычно 5–10 секунд'}
        </p>
      </div>
    );
  }
  if (!questions || questions.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-tg-hint">Для этого урока теста нет.</p>
        <Button variant="secondary" className="mt-4" onClick={() => nav(-1)}>
          Назад
        </Button>
      </div>
    );
  }

  if (finished) {
    const graduated = examMode && finished.passed;
    const improved =
      finished.prevBestScore != null && finished.scoreAvg > finished.prevBestScore;
    return (
      <div className="safe-bottom min-h-full">
        <TopBar title={graduated ? 'Курс пройден' : 'Результат теста'} />
        <div className="p-4 space-y-4">
          {graduated ? (
            <Card className="text-center py-10 border-2 border-brand-orange/40 bg-brand-orange/5">
              <div className="text-6xl">🏆</div>
              <div className="text-5xl font-bold mt-4">{finished.scoreAvg.toFixed(1)}/10</div>
              <div className="mt-3 text-xl font-bold text-brand-orange">Курс пройден!</div>
              <p className="mt-2 text-sm text-tg-hint max-w-xs mx-auto">
                Поздравляем — ты успешно сдал финальный экзамен и завершил обучение. 🎓
              </p>
            </Card>
          ) : (
            <Card className="text-center py-8">
              {finished.passed ? (
                <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
              ) : (
                <XCircle className="w-16 h-16 mx-auto text-red-500" />
              )}
              <div className="text-4xl font-bold mt-4">{finished.scoreAvg.toFixed(1)}/10</div>
              <div className="mt-2 text-tg-hint">
                {finished.passed ? '🎉 Сдано! Следующий урок открыт.' : 'Не сдано. Попробуй ещё раз.'}
              </div>
              <div className="mt-3 text-xs text-tg-hint">
                Попытка №{finished.attemptNo}
                {finished.prevBestScore != null && (
                  <>
                    {' · '}прошлый лучший {finished.prevBestScore.toFixed(1)}/10
                    {improved && <span className="text-green-500"> ↑</span>}
                  </>
                )}
              </div>
            </Card>
          )}

          {/* Разбор ответов */}
          {review.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <button
                onClick={() => setReviewOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 font-semibold"
              >
                <span>Разбор ответов ({review.length})</span>
                {reviewOpen ? (
                  <ChevronUp className="w-5 h-5 text-tg-hint" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-tg-hint" />
                )}
              </button>
              {reviewOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {review.map((e, i) => {
                    const wrong = e.score < 7;
                    return (
                      <div
                        key={e.questionId}
                        className={
                          'rounded-xl border p-3 ' +
                          (wrong
                            ? 'border-red-500/40 bg-red-500/5'
                            : 'border-green-500/30 bg-green-500/5')
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium whitespace-pre-wrap">
                            <span className="text-tg-hint mr-1">{i + 1}.</span>
                            {e.questionMd}
                          </p>
                          <span
                            className={
                              'shrink-0 text-xs font-bold px-2 py-1 rounded-lg ' +
                              (wrong ? 'bg-red-500/15 text-red-500' : 'bg-green-500/15 text-green-600')
                            }
                          >
                            {e.score}/10
                          </span>
                        </div>

                        {/* Варианты (MC) с подсветкой выбранного/правильного */}
                        {e.options && e.options.length >= 2 ? (
                          <div className="mt-2 space-y-1">
                            {e.options.map((opt, oi) => {
                              const isCorrect = e.correctOption === oi;
                              const isPicked = e.selectedOption === oi;
                              return (
                                <div
                                  key={oi}
                                  className={
                                    'text-sm px-2 py-1.5 rounded-lg flex items-center gap-2 ' +
                                    (isCorrect
                                      ? 'bg-green-500/15 text-green-700'
                                      : isPicked
                                        ? 'bg-red-500/15 text-red-600'
                                        : 'text-tg-hint')
                                  }
                                >
                                  <span className="font-mono text-xs">
                                    {String.fromCharCode(65 + oi)}.
                                  </span>
                                  <span className="flex-1">{opt}</span>
                                  {isCorrect && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                                  {isPicked && !isCorrect && (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="mt-2 text-sm">
                            <span className="text-tg-hint">Твой ответ: </span>
                            <span className="whitespace-pre-wrap">{e.userAnswer || '—'}</span>
                          </div>
                        )}

                        {e.feedback && (
                          <p className="mt-2 text-sm text-tg-hint whitespace-pre-wrap">
                            {e.feedback}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Основное действие */}
          {graduated ? (
            <Button variant="primary" size="lg" className="w-full" onClick={() => nav('/training')}>
              На главную курса
            </Button>
          ) : finished.passed && finished.nextLessonId ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => nav(`/training/lesson/${finished.nextLessonId}`)}
            >
              Открыть следующий урок →
            </Button>
          ) : (
            <Button variant="primary" size="lg" className="w-full" onClick={() => nav('/training')}>
              Вернуться к курсу
            </Button>
          )}

          {!graduated && finished.passed && finished.nextLessonId && (
            <Button variant="secondary" className="w-full" onClick={() => nav('/training')}>
              Вернуться к курсу
            </Button>
          )}

          {!finished.passed && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                setAttemptId(null);
                setIdx(0);
                setAnswer('');
                setSelected(null);
                setShowFreeForm(false);
                setLastFeedback(null);
                setFinished(null);
                setReview([]);
                setReviewOpen(false);
              }}
            >
              Повторить тест
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!q) return null;
  const isLast = idx === questions.length - 1;

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title={examMode ? 'Финальный экзамен' : 'AI-тест'}
        subtitle={`Вопрос ${idx + 1}/${questions.length}${examMode ? ` · проходной ${passScorePct(EXAM_PASS_SCORE)}%` : ''}`}
      />
      <div className="p-4 space-y-4">
        <Card className="space-y-3">
          <div className="text-sm text-tg-hint">Вопрос</div>
          <p className="text-lg font-medium whitespace-pre-wrap">{q.questionMd}</p>
        </Card>

        {!lastFeedback && (
          <>
            {isMC && !showFreeForm && (
              <Card className="space-y-2">
                {(q.options as string[]).map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => setSelected(i)}
                    className={
                      'w-full text-left px-3 py-3 rounded-xl border-2 transition-all ' +
                      (selected === i
                        ? 'border-brand-orange bg-brand-orange/10'
                        : 'border-tg-secondaryBg hover:bg-tg-secondaryBg/60')
                    }
                  >
                    <span className="font-mono text-sm text-tg-hint mr-2">
                      {String.fromCharCode(65 + i)}.
                    </span>
                    {opt}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setShowFreeForm(true);
                    setSelected(null);
                  }}
                  className="w-full text-sm text-tg-hint py-2 hover:text-brand-orange"
                >
                  Не подходит? — Свой ответ
                </button>
              </Card>
            )}

            {(!isMC || showFreeForm) && (
              <Card className="space-y-2">
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  className="w-full min-h-[120px] bg-tg-bg rounded-xl p-3 text-tg-text outline-none resize-none"
                  placeholder="Свободный ответ… или запиши голосом 🎤"
                  disabled={submit.isPending || voice.recording || voice.busy}
                />
                <div className="flex items-center gap-2">
                  {voice.recording ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-red-500/10 text-red-500"
                      onClick={voice.stop}
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Стоп
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={voice.start}
                      disabled={voice.busy}
                    >
                      {voice.busy ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Mic className="w-4 h-4 mr-1" />
                      )}
                      {voice.busy ? 'Расшифровываю…' : 'Голосом'}
                    </Button>
                  )}
                  {isMC && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setShowFreeForm(false);
                        setAnswer('');
                      }}
                    >
                      ← К вариантам
                    </Button>
                  )}
                </div>
              </Card>
            )}

            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={
                submit.isPending ||
                !attemptId ||
                (isMC && !showFreeForm && selected === null) ||
                ((!isMC || showFreeForm) && answer.trim().length < 3)
              }
              onClick={() => submit.mutate()}
            >
              {submit.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Проверить
            </Button>
          </>
        )}

        {lastFeedback && (
          <>
            <Card
              className={
                lastFeedback.score >= 7
                  ? 'border-2 border-green-500/40 bg-green-500/5'
                  : 'border-2 border-red-500/40 bg-red-500/5'
              }
            >
              <div className="flex items-center gap-2 font-semibold">
                {lastFeedback.score >= 7 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                Оценка: {lastFeedback.score}/10
              </div>
              <p className="text-sm mt-2 whitespace-pre-wrap">{lastFeedback.feedback}</p>
            </Card>

            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={finish.isPending}
              onClick={() => {
                if (isLast) {
                  finish.mutate();
                } else {
                  setIdx(idx + 1);
                  setAnswer('');
                  setSelected(null);
                  setShowFreeForm(false);
                  setLastFeedback(null);
                }
              }}
            >
              {isLast ? 'Завершить тест' : 'Следующий вопрос →'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
